import { useFlags } from "launchdarkly-react-client-sdk";
import { useEffect, useMemo, useState } from "react";
import { useController } from "react-hook-form";

import ExpandMoreOutlinedIcon from "@mui/icons-material/ExpandMoreOutlined";
import SearchIcon from "@mui/icons-material/Search";
import Autocomplete from "@mui/material/Autocomplete";
import Box from "@mui/material/Box";
import TextField from "@mui/material/TextField";
import type { SxProps } from "@mui/material/styles";

import { useDebounce } from "hooks";

import PatientSearchOption from "components/patient-search/patient-search-option.component";
import { SHOW_INACTIVE_PATIENTS_SEARCH } from "constants/feature-flags";
import { PATIENT_SEARCH_QUERY, useQueryPatientsQuery } from "services/patients";
import type { Patient } from "services/types/patients.type";

type PatientFieldProps = {
  disabled?: boolean;
  onOptionSelected?: (patient: Patient | null) => void;
  transformOption?: (option: Patient) => Patient;
  required?: boolean;
  showIcon?: boolean;
  preferredClinic?: number;
  control?: any;
  fieldName?: string;
  label?: string;
  hideAvatar?: boolean;
  placeholder?: string;
  shouldUnregister?: boolean;
  sx?: SxProps;
};

const getOptionLabel = (option: Patient) => {
  return [option.first_name, option.middle_name, option.last_name]
    .filter((namePart) => Boolean(namePart))
    .join(" ");
};

export default function PatientFieldWithSearch({
  disabled = false,
  required = false,
  showIcon = false,
  hideAvatar = false,
  preferredClinic,
  control,
  fieldName = "patient",
  label = "Patient Search",
  onOptionSelected,
  transformOption = (option: Patient) => option,
  placeholder,
  shouldUnregister,
  sx
}: PatientFieldProps) {
  const flags = useFlags();
  const showInactivePatients = flags[SHOW_INACTIVE_PATIENTS_SEARCH];
  const rules = useMemo(
    () => ({
      required: required ? "Patient is required" : false
    }),
    [required]
  );
  const [patientSearch, setPatientSearch] = useState<string>("");
  const {
    field: { onChange, onBlur, value: selectedValue, ref },
    formState: { errors }
  } = useController({
    name: fieldName,
    control,
    rules,
    shouldUnregister,
    defaultValue: undefined
  });
  const { value: dPatientSearch, isDebouncing } = useDebounce(
    patientSearch,
    200
  );
  const { data, isLoading, isFetching, isUninitialized, refetch } =
    useQueryPatientsQuery(
      {
        search: dPatientSearch,
        baseFilter: preferredClinic
          ? { preferred_clinic: preferredClinic }
          : {},
        is_active: showInactivePatients ? undefined : true,
        query: PATIENT_SEARCH_QUERY
      },
      { skip: dPatientSearch.length < 3 }
    );

  const [options, setOptions] = useState<Patient[] | undefined>();

  useEffect(() => {
    if (
      dPatientSearch.length >= 3 &&
      !isLoading &&
      !isFetching &&
      !isUninitialized
    ) {
      setOptions(data);
    } else {
      setOptions([]);
    }
  }, [dPatientSearch.length, data, isFetching, isLoading, isUninitialized]);

  return (
    <Box
      display="flex"
      justifyContent="space-between"
      alignItems="center"
      sx={sx}
    >
      {showIcon && <SearchIcon sx={{ mt: 0.5, mr: 1.5 }} color="action" />}
      <Autocomplete
        disabled={disabled}
        key={selectedValue?.id || label}
        fullWidth
        popupIcon={<ExpandMoreOutlinedIcon />}
        inputValue={patientSearch}
        getOptionLabel={(option) => getOptionLabel(option)}
        onInputChange={(_event, searchValue) => setPatientSearch(searchValue)}
        isOptionEqualToValue={(option, value) => option.id === value?.id}
        renderInput={(params) => (
          <TextField
            {...params}
            required={required}
            {...(Boolean(errors?.[fieldName]?.message)
              ? {
                  error: true,
                  helperText: errors?.[fieldName]?.message as string
                }
              : {})}
            label={label}
            placeholder={placeholder}
          />
        )}
        renderOption={(props, option: Patient) => (
          <PatientSearchOption
            key={option.id}
            renderProps={props}
            option={option}
            allowAddNewPatient
            hideAvatar={hideAvatar}
          />
        )}
        value={selectedValue}
        onFocus={() => {
          if (!isUninitialized) {
            refetch();
          }
          setTimeout(() => {
            setOptions([]);
          }, 1);
        }}
        onBlur={() => {
          setOptions([]);
          onBlur();
        }}
        ref={ref}
        onChange={(_event, value) => {
          onChange(value);
          onBlur();
          onOptionSelected?.(value);
        }}
        options={options?.map(transformOption) || []}
        loading={isLoading || isFetching || isDebouncing}
        filterOptions={(options) => {
          return options.map((option) => option);
        }}
        slotProps={{
          popper: {
            sx: {
              zIndex: 14001
            }
          }
        }}
      />
    </Box>
  );
}
